// External Dependencies
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Cookies from 'js-cookie'

// Icons
import { IoArrowBackOutline, IoClose, IoLink, IoBusiness } from 'react-icons/io5'
import { FiUpload, FiChevronDown } from 'react-icons/fi'
import { MdOpenInNew } from 'react-icons/md'

// Components
import PageHeader from '../../components/Custom/PageHeader/PageHeader'
import CustomToast from '../../components/Custom/CustomToast'

// Hooks and Utils
import useGoBack from '../../hooks/useGoBack'
import useUserStore from '../../utils/state/useUserStore'
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'
import configManager from '../../config/configManager'

// Constants
const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/jpg']
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const PAGE_SIZE = 20

// Custom Multi-Select Dropdown Component
const MultiSelect = ({
  label,
  options,
  value = [],
  onChange,
  error,
  placeholder,
  loading,
  hasMore,
  onLoadMore,
  disabled = false,
  required = false,
  getOptionLabel,
  getOptionValue,
  loadingText = 'Loading more...',
  noOptionsText = 'No options available',
  loadMoreText = 'Load More',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const listRef = useRef(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Filter options based on search
  const filteredOptions = searchTerm
    ? options.filter(opt =>
      getOptionLabel(opt).toLowerCase().includes(searchTerm.toLowerCase())
    )
    : options

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle scroll for infinite loading
  const handleListScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    if (scrollTop + clientHeight >= scrollHeight * 0.8 && !loading && hasMore) {
      onLoadMore?.()
    }
  }

  const handleToggleOption = (option) => {
    const optionValue = getOptionValue(option)
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue))
    } else {
      onChange([...value, optionValue])
    }
  }

  const removeOption = (optionValue) => {
    onChange(value.filter(v => v !== optionValue))
  }

  const selectedOptions = options.filter(opt => value.includes(getOptionValue(opt)))

  return (
    <div className='w-full' ref={dropdownRef}>
      {label && (
        <label className='block text-sm font-medium mb-2'>
          {label} {required && <span className='text-red-500'>*</span>}
        </label>
      )}
      <div className='relative'>
        {/* Trigger Button */}
        <button
          type='button'
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full border-[2px] ${error ? 'border-red-500' : 'border-light-gray'} 
            rounded-lg p-3 focus:outline-none focus:border-prime 
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'}
            flex items-center justify-between text-left min-h-[52px]`}
        >
          <div className='flex flex-wrap gap-1.5 flex-1'>
            {selectedOptions.length > 0 ? (
              selectedOptions.map(opt => (
                <span
                  key={getOptionValue(opt)}
                  className='inline-flex items-center gap-1 bg-prime/10 text-prime px-2 py-1 rounded-md text-sm'
                >
                  {getOptionLabel(opt)}
                  {!disabled && (
                    <button
                      type='button'
                      onClick={(e) => {
                        e.stopPropagation()
                        removeOption(getOptionValue(opt))
                      }}
                      className='hover:text-red-500'
                    >
                      <IoClose size={14} />
                    </button>
                  )}
                </span>
              ))
            ) : (
              <span className='text-gray-400'>{placeholder || 'Select...'}</span>
            )}
          </div>
          <FiChevronDown className={`transition-transform ml-2 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown List */}
        {isOpen && !disabled && (
          <div className='absolute z-50 w-full mt-1 bg-white border border-light-gray rounded-lg shadow-lg overflow-hidden'>
            {/* Search Input */}
            <div className='p-2 border-b border-light-gray'>
              <input
                type='text'
                placeholder='Search...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full border rounded-lg p-2 text-sm focus:outline-none focus:border-prime'
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Options List */}
            <ul
              ref={listRef}
              onScroll={handleListScroll}
              className='max-h-60 overflow-y-auto'
            >
              {filteredOptions.length === 0 ? (
                <li className='px-4 py-2 text-gray-500 text-sm text-center'>
                  {searchTerm ? 'No matching options' : noOptionsText}
                </li>
              ) : (
                filteredOptions.map((option, index) => {
                  const optionValue = getOptionValue(option)
                  const isSelected = value.includes(optionValue)
                  return (
                    <li
                      key={optionValue || index}
                      onClick={() => handleToggleOption(option)}
                      className={`px-4 py-2 text-sm cursor-pointer hover:bg-prime/10 transition-colors flex items-center gap-2
                        ${isSelected ? 'bg-prime/10 text-prime' : 'text-gray-700'}`}
                    >
                      <input
                        type='checkbox'
                        checked={isSelected}
                        onChange={() => { }}
                        className='w-4 h-4 text-prime border-gray-300 rounded focus:ring-prime'
                      />
                      {getOptionLabel(option)}
                    </li>
                  )
                })
              )}

              {/* Load More Button */}
              {hasMore && !loading && filteredOptions.length > 0 && (
                <li>
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation()
                      onLoadMore?.()
                    }}
                    className='w-full px-4 py-2 text-sm text-prime hover:bg-prime/5 transition-colors border-t border-light-gray font-medium'
                  >
                    {loadMoreText}
                  </button>
                </li>
              )}

              {/* Loading Indicator */}
              {loading && (
                <li className='px-4 py-2 text-gray-400 text-sm text-center flex items-center justify-center gap-2'>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-prime'></div>
                  {loadingText}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
      {error && <p className='text-red-500 text-sm mt-1'>{error}</p>}
    </div>
  )
}

const MaterialUpdate = () => {
  // Hooks
  const navigate = useNavigate()
  const { id } = useParams()
  const GoBack = useGoBack()
  const configData = configManager()
  const userPermissions = useUserStore((state) => state.user) || []
  const fileInputRef = useRef(null)

  // State Management
  const [brands, setBrands] = useState([])
  const [patterns, setPatterns] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isUploading, setIsUploading] = useState(false)

  // Pagination states for Patterns
  const [patternPage, setPatternPage] = useState(1)
  const [patternHasMore, setPatternHasMore] = useState(true)
  const [patternLoading, setPatternLoading] = useState(false)

  const [formData, setFormData] = useState({
    materialName: '',
    materialDescription: '',
    selectedCategory: 'Select Category',
    selectedBrand: '',
    selectedPatterns: [], // Array of pattern IDs (numbers)
    materialPreview: null,
    materialFile: null,
    realwidth_mm: '0',
    pixelwidth: '',
    realheight: '',
    // Auto-filled brand fields
    brands_name: '',
    brands_website_link: '',
    brands_logo: '',
  })
  const [errors, setErrors] = useState({})

  // Memoized Categories
  const categories = useMemo(
    () => [
      { value: 'Stone', label: 'Stone' },
      { value: 'Brick', label: 'Brick' },
      { value: 'Wood', label: 'Wood' },
      { value: 'Terrazzo', label: 'Terrazzo' },
      { value: 'Concrete', label: 'Concrete' },
      { value: 'Metal', label: 'Metal' },
      { value: 'Tile', label: 'Tile' },
      { value: 'Fabric', label: 'Fabric' },
      { value: 'Wallpaper', label: 'Wallpaper' },
      { value: 'Carpet', label: 'Carpet' },
      { value: 'Surfaces', label: 'Surfaces' },
      { value: 'Finishes', label: 'Finishes' },
      { value: 'Landscaping', label: 'Landscaping' },
      { value: 'Insulation', label: 'Insulation' },
      { value: 'Organic', label: 'Organic' },
      { value: 'Acoustic', label: 'Acoustic' },
    ],
    [],
  )

  // ---------- Fetch Patterns with Pagination ----------
  const fetchPatterns = async (page = 1, append = true) => {
    if (patternLoading) return
    setPatternLoading(true)
    try {
      const response = await axiosInterceptorInstance.post(`/patterns?page=${page}&limit=${PAGE_SIZE}&front=false`, {
        front: false
      })
      const results = response.data?.data?.results || response.data?.results || []
      const totalPages = response.data?.data?.totalPages || response.data?.totalPages || 1

      if (append) {
        const existingIds = new Set(patterns.map(item => item.id))
        const uniqueNew = results.filter(item => !existingIds.has(item.id))
        setPatterns(prev => [...prev, ...uniqueNew])
      } else {
        setPatterns(results)
      }

      setPatternHasMore(page < totalPages)
      setPatternPage(page)
    } catch (err) {
      CustomToast({ message: 'Failed to load patterns.', type: 'error' })
    } finally {
      setPatternLoading(false)
    }
  }

  // Load more patterns
  const loadMorePatterns = useCallback(() => {
    if (!patternLoading && patternHasMore) {
      fetchPatterns(patternPage + 1, true)
    }
  }, [patternLoading, patternHasMore, patternPage])

  // Load Brands
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axiosInterceptorInstance.post('/brands', {
          page: 1,
          limit: 200
        })
        const brandsData = response.data?.data?.results || response.data?.results || []
        setBrands(brandsData)
      } catch (err) {
        CustomToast({ message: 'Failed to load brands.', type: 'error' })
      }
    }
    fetchBrands()
    fetchPatterns(1, false)
  }, [])

  // Fetch Material Data
  useEffect(() => {
    const fetchMaterialData = async () => {
      try {
        const response = await axiosInterceptorInstance.get(
          `/materials/details/${id}`,
        )
        if (!response.data.success) {
          throw new Error('Failed to fetch material data')
        }

        const materialData = response.data.data

        // Get brand ID from material data
        const brandId = materialData.brand?._id || materialData.brand || ''
        const brandName = materialData.brands_name || materialData.brand?.name || ''
        const brandWebsite = materialData.brands_website_link || materialData.brand?.website_link || ''
        const brandLogo = materialData.brands_logo || materialData.brand?.logo || ''

        // Get patterns from material data - convert to numbers
        let patternIds = []
        if (materialData.patterns && Array.isArray(materialData.patterns)) {
          patternIds = materialData.patterns.map(p => Number(p.id || p))
        }

        setFormData({
          materialName: materialData.name || '',
          materialDescription: materialData.description || '',
          selectedCategory: materialData.category || 'Select Category',
          selectedBrand: brandId,
          selectedPatterns: patternIds,
          materialPreview:
            materialData.thumbnail || materialData.source_names?.[0] || null,
          materialFile: null,
          realwidth_mm: materialData.realwidth || '2000',
          pixelwidth: materialData.pixelwidth || '',
          realheight: materialData.realheight || '',
          brands_name: brandName,
          brands_website_link: brandWebsite,
          brands_logo: brandLogo,
        })
      } catch (error) {
        CustomToast({
          message: 'Failed to fetch material data. Please try again!',
          type: 'error',
        })
        navigate('/material')
      } finally {
        setIsLoading(false)
      }
    }

    fetchMaterialData()
  }, [id, navigate])

  // Handle Brand Change - Auto-fill brand fields
  const handleBrandChange = (e) => {
    const brandId = e.target.value
    const selectedBrand = brands.find(b => b._id === brandId)

    if (selectedBrand) {
      setFormData((prev) => ({
        ...prev,
        selectedBrand: brandId,
        brands_name: selectedBrand.name || '',
        brands_website_link: selectedBrand.website_link || '',
        brands_logo: selectedBrand.logo || '',
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        selectedBrand: '',
        brands_name: '',
        brands_website_link: '',
        brands_logo: '',
      }))
    }
    setErrors((prev) => ({ ...prev, brand: null }))
  }

  // Handle manual brand name update
  const handleBrandNameChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      brands_name: e.target.value,
    }))
  }

  // Handle manual brand website update
  const handleBrandWebsiteChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      brands_website_link: e.target.value,
    }))
  }

  // Handle Patterns Change - Convert to Numbers
  const handlePatternsChange = (selectedPatterns) => {
    const patternIdsAsNumbers = selectedPatterns.map(id => Number(id))
    setFormData((prev) => ({
      ...prev,
      selectedPatterns: patternIdsAsNumbers,
    }))
    setErrors((prev) => ({ ...prev, patterns: null }))
  }

  // Form Validation
  const validateForm = () => {
    const newErrors = {}

    if (!formData.materialName.trim()) {
      newErrors.materialName = 'Material name is required.'
    }

    if (!formData.materialDescription.trim()) {
      newErrors.materialDescription = 'Material description is required.'
    }

    if (formData.selectedCategory === 'Select Category') {
      newErrors.category = 'Please select a valid category.'
    }

    if (!formData.materialPreview) {
      newErrors.file = 'Please upload a material file.'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // File Handling
  const handleImageChange = (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        file: 'Invalid file type. Only JPG, PNG, and JPEG are accepted.',
      }))
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      setErrors((prev) => ({
        ...prev,
        file: 'File size must be less than 5MB.',
      }))
      return
    }

    setFormData((prev) => ({
      ...prev,
      materialFile: file,
      materialPreview: URL.createObjectURL(file),
    }))
    setErrors((prev) => ({ ...prev, file: null }))
  }

  const handleDelete = () => {
    setFormData((prev) => ({
      ...prev,
      materialFile: null,
      materialPreview: null,
    }))
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // ---------- Form Submission - Send as Individual Fields ----------
  const handleSubmit = async () => {
    if (!userPermissions.includes('material:update')) {
      CustomToast({
        message: 'You do not have permission to update materials',
        type: 'error',
      })
      return
    }

    if (!validateForm()) return

    setIsUploading(true)

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('name', formData.materialName)
      formDataToSend.append('description', formData.materialDescription)
      formDataToSend.append('category', formData.selectedCategory)
      formDataToSend.append('type', 'Material')
      formDataToSend.append('realwidth_mm', formData.realwidth_mm || '2000')
      formDataToSend.append('pixelwidth', formData.pixelwidth || '0')
      formDataToSend.append('realheight', formData.realheight || '0')

      // ---------- Patterns - Send as Individual Fields ----------
      if (formData.selectedPatterns && formData.selectedPatterns.length > 0) {
        formData.selectedPatterns.forEach((patternId, index) => {
          formDataToSend.append(`patterns[${index}]`, patternId)
        })
      }

      // Brand fields - send updated name and website
      formDataToSend.append('brands_name', formData.brands_name || '3d')
      formDataToSend.append('brands_website_link', formData.brands_website_link || '')
      formDataToSend.append('brands_logo', formData.brands_logo || '')
      formDataToSend.append('brand_id', formData.selectedBrand || '')

      if (formData.materialFile) {
        formDataToSend.append('file', formData.materialFile)
      }

      await axiosInterceptorInstance.patch(
        `/materials/update/${id}`,
        formDataToSend,
      )

      CustomToast({
        message: 'Material updated successfully.',
        type: 'success',
      })
      navigate('/material')
    } catch (error) {
      CustomToast({
        message:
          error.response?.data?.message ||
          'Failed to update material. Please try again!',
        type: 'error',
      })
    } finally {
      setIsUploading(false)
    }
  }

  // Helper function to get image URL
  const getImageUrl = (path) => {
    if (!path) return null
    if (path.includes('blob')) return path
    if (path.startsWith('/')) {
      return `${configData.CDN_BASE_URL}${path}`
    }
    return `${configData.CDN_BASE_URL}/thumbnails/${path}`
  }

  // Helper function to get brand logo URL
  const getBrandLogoUrl = (logo) => {
    if (!logo) return null
    if (logo.startsWith('/')) {
      return `${configData.CDN_BASE_URL}${logo}`
    }
    if (logo.startsWith('http')) {
      return logo
    }
    return `${configData.CDN_BASE_URL}/brands/${logo}`
  }

  // Helper functions for MultiSelect
  const getPatternLabel = (p) => p.name || p.id || p._id
  const getPatternValue = (p) => {
    const val = p.id || p._id
    return Number(val)
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-prime' />
      </div>
    )
  }

  const brandLogoUrl = getBrandLogoUrl(formData.brands_logo)

  const getEditorLink = () => {
    const token = Cookies.get('admin_token') || ''
    const params = new URLSearchParams({ material_id: id })
    if (token) params.set('admin_token', token)
    return `${configData.BACKEND_URL}/create?${params.toString()}`
  }

  return (
    <div>
      <PageHeader>
        <div className='flex gap-2 items-center justify-between w-full'>
          <div className='flex gap-2 items-center font-medium'>
            <button
              onClick={GoBack}
              className='h-9 w-9 flex justify-center items-center rounded-full text-xl cursor-pointer transition-all duration-300 hover:text-white hover:bg-prime'
              aria-label='Go back'
            >
              <IoArrowBackOutline />
            </button>
            <h1 className='text-2xl'>Update Material</h1>
          </div>
          <a
            href={getEditorLink()}
            target='_blank'
            rel='noopener noreferrer'
            className='flex items-center gap-2 px-4 py-2 bg-prime text-white text-sm font-medium rounded-lg hover:bg-prime/90 transition-all'
          >
            Open in Editor <MdOpenInNew className='text-base' />
          </a>
        </div>
      </PageHeader>

      <div className='space-y-6 flex flex-col items-center pb-16'>
        {/* Category Selection */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2' htmlFor='category'>
            Select Category:
          </label>
          <select
            id='category'
            className={`w-full border-[2px] ${errors.category ? 'border-red-500' : 'border-light-gray'
              } rounded-lg p-3 focus:outline-none focus:border-prime`}
            value={formData.selectedCategory}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                selectedCategory: e.target.value,
              }))
              setErrors((prev) => ({ ...prev, category: null }))
            }}
          >
            <option disabled value='Select Category'>
              Select Category
            </option>
            {categories.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
          {errors.category && (
            <p className='text-red-500 text-sm mt-1'>{errors.category}</p>
          )}
        </div>

        {/* Brand Selection */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2' htmlFor='brand'>
            Select Brand: <span className='text-xs text-gray-400 font-normal'>(Optional)</span>
          </label>
          <select
            id='brand'
            className={`w-full border-[2px] ${errors.brand ? 'border-red-500' : 'border-light-gray'
              } rounded-lg p-3 focus:outline-none focus:border-prime`}
            value={formData.selectedBrand}
            onChange={handleBrandChange}
          >
            <option value=''>Select Brand (Optional)</option>
            {brands.map((brand) => (
              <option key={brand._id} value={brand._id}>
                {brand.name || brand._id}
              </option>
            ))}
          </select>
          {errors.brand && (
            <p className='text-red-500 text-sm mt-1'>{errors.brand}</p>
          )}
        </div>

        {/* Brand Details Display */}
        {(formData.brands_name || formData.brands_website_link || brandLogoUrl) && (
          <div className='w-full sm:w-[500px] p-4 bg-gray-50 border border-gray-200 rounded-lg'>
            <h3 className='text-sm font-medium text-gray-700 mb-3'>Brand Details</h3>

            <div className='space-y-3'>
              {/* Brand Logo */}
              {brandLogoUrl && (
                <div className='flex items-center gap-3'>
                  <span className='text-sm text-gray-500 w-24'>Logo:</span>
                  <img
                    src={brandLogoUrl}
                    alt={formData.brands_name || 'Brand logo'}
                    className='h-10 object-contain border border-gray-200 rounded p-1 bg-white'
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                </div>
              )}

              {/* Brand Name - Editable */}
              <div className='flex items-center gap-3'>
                <span className='text-sm text-gray-500 w-24 flex items-center gap-1'>
                  <IoBusiness className='text-gray-400' />
                  Name:
                </span>
                <input
                  type='text'
                  value={formData.brands_name}
                  onChange={handleBrandNameChange}
                  placeholder='Brand name'
                  className='flex-1 border-[2px] border-light-gray rounded-lg p-2 focus:outline-none focus:border-prime text-sm'
                />
              </div>

              {/* Brand Website - Editable */}
              <div className='flex items-center gap-3'>
                <span className='text-sm text-gray-500 w-24 flex items-center gap-1'>
                  <IoLink className='text-gray-400' />
                  Website:
                </span>
                <input
                  type='url'
                  value={formData.brands_website_link}
                  onChange={handleBrandWebsiteChange}
                  placeholder='https://example.com'
                  className='flex-1 border-[2px] border-light-gray rounded-lg p-2 focus:outline-none focus:border-prime text-sm'
                />
                {formData.brands_website_link && (
                  <a
                    href={formData.brands_website_link}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-prime hover:underline text-sm whitespace-nowrap'
                  >
                    Visit
                  </a>
                )}
              </div>

              <div className='text-xs text-gray-400 mt-2'>
                <p>💡 Brand name and website can be updated. Logo is read-only.</p>
              </div>
            </div>
          </div>
        )}

        {/* Material Name */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2'>
            Material Name:
          </label>
          <input
            type='text'
            placeholder='Material Name'
            className={`w-full border-[2px] ${errors.materialName ? 'border-red-500' : 'border-light-gray'
              } rounded-lg p-3 focus:outline-none focus:border-prime`}
            value={formData.materialName}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                materialName: e.target.value,
              }))
              setErrors((prev) => ({ ...prev, materialName: null }))
            }}
          />
          {errors.materialName && (
            <p className='text-red-500 text-sm mt-1'>{errors.materialName}</p>
          )}
        </div>

        {/* Material Description */}
        <div className='w-full sm:w-[500px]'>
          <label
            className='block text-sm font-medium mb-2'
            htmlFor='materialDescription'
          >
            Material Description:
          </label>
          <textarea
            id='materialDescription'
            className={`w-full border-[2px] ${errors.materialDescription
              ? 'border-red-500'
              : 'border-light-gray'
              } rounded-lg p-3 focus:outline-none focus:border-prime`}
            value={formData.materialDescription}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                materialDescription: e.target.value,
              }))
              setErrors((prev) => ({ ...prev, materialDescription: null }))
            }}
            rows={4}
          />
          {errors.materialDescription && (
            <p className='text-red-500 text-sm mt-1'>
              {errors.materialDescription}
            </p>
          )}
        </div>

        {/* Patterns Selection - Multi Select */}
        <div className='w-full sm:w-[500px]'>
          <MultiSelect
            label='Select Patterns:'
            options={patterns}
            value={formData.selectedPatterns}
            onChange={handlePatternsChange}
            placeholder='Search and select patterns...'
            loading={patternLoading}
            hasMore={patternHasMore}
            onLoadMore={loadMorePatterns}
            getOptionLabel={getPatternLabel}
            getOptionValue={getPatternValue}
            loadingText='Loading more patterns...'
            loadMoreText='Load More Patterns'
            noOptionsText='No patterns available'
          />
        </div>

        {/* Tile Physical Width */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2' htmlFor='realwidth_mm'>
            Tile Physical Width (mm):
          </label>
          <input
            id='realwidth_mm'
            type='number'
            min='1'
            max='99999'
            placeholder='e.g. 2000'
            className='w-full border-[2px] border-light-gray rounded-lg p-3 focus:outline-none focus:border-prime'
            value={formData.realwidth_mm}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, realwidth_mm: e.target.value }))
            }
          />
          <div className='mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600 space-y-1.5'>
            <p className='font-semibold text-gray-700'>
              💡 How to Set the Physical Width
            </p>
            <ul className='list-disc pl-4 space-y-1'>
              <li>
                <strong>Single Tile / Plank:</strong> If the image contains only one tile or plank, enter its actual physical width (e.g., <strong>300 mm</strong> or <strong>600 mm</strong>).
              </li>
              <li>
                <strong>Seamless Pattern:</strong> If the image shows multiple repeating tiles or bricks, enter the total width covered by the visible pattern (e.g., <strong>1000–1500 mm</strong>).
              </li>
              <li>
                <strong>Large Surfaces (Concrete, Grass, Asphalt):</strong> For large seamless textures, use a standard physical width between <strong>2500–4000 mm</strong> (e.g., <strong>3000 mm</strong>).
              </li>
            </ul>
          </div>
        </div>

        {/* Material Upload */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2'>
            Upload Material: <span className='text-xs text-gray-400 font-normal'>(leave empty to keep existing)</span>
          </label>
          <div
            className={`relative w-full aspect-[9/5] rounded-lg bg-primary bg-opacity-5 cursor-pointer border ${errors.file ? 'border-red-500' : 'border-dashed border-prime'
              } flex items-center justify-center`}
          >
            {!formData.materialPreview && (
              <input
                accept={ACCEPTED_FILE_TYPES.join(',')}
                className='absolute inset-0 opacity-0 cursor-pointer'
                type='file'
                ref={fileInputRef}
                onChange={handleImageChange}
              />
            )}
            {formData.materialPreview ? (
              <img
                src={formData.materialPreview.includes('blob')
                  ? formData.materialPreview
                  : getImageUrl(formData.materialPreview)
                }
                alt='Material Preview'
                className='absolute top-0 left-0 object-cover w-full h-full rounded-lg'
              />
            ) : (
              <div className='flex flex-col gap-4 items-center'>
                <FiUpload className='text-4xl text-white bg-prime rounded-full cursor-pointer p-2' />
                <span className='text-prime'>
                  Only JPG, PNG, and JPEG files are accepted
                </span>
              </div>
            )}
            {(formData.materialFile || formData.materialPreview) && (
              <button
                type='button'
                onClick={handleDelete}
                className='absolute top-2 right-2 flex items-center justify-center w-8 h-8 bg-prime bg-opacity-80 rounded-full text-primary bg-white'
                aria-label='Remove image'
              >
                <IoClose />
              </button>
            )}
          </div>
          {errors.file && (
            <p className='text-red-500 text-sm mt-1'>{errors.file}</p>
          )}
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={isUploading}
          className='w-full sm:w-[500px] py-3 rounded-md text-prime bg-white border border-prime hover:bg-prime hover:text-white transition-all duration-300 disabled:bg-prime disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-opacity-80 disabled:text-white'
        >
          {isUploading ? 'Updating...' : 'Update Material'}
        </button>
      </div>
    </div>
  )
}

export default MaterialUpdate